import moment from "moment/moment";

const BACKGROUND_Z_INDEX = 0;
const FOREGROUND_Z_INDEX = 3;
const originalIndexKey = Symbol("calendarOriginalIndex");

const getEventTime = (event, point) => new Date(event[point]).getTime();

const getEventDuration = (event) =>
  getEventTime(event, "end") - getEventTime(event, "start");

const getEventDayKey = (event) => moment(event.start).format("YYYY-MM-DD");

const getEventGroupKey = (event) =>
  `${String(event.resourceId)}-${getEventDayKey(event)}`;

const isAvailabilityTime = (event) => event.availabilityGrid;

const isForegroundEvent = (event) => !isAvailabilityTime(event);

const groupByResourceAndDay = (events = []) =>
  events.reduce((acc, event) => {
    const key = getEventGroupKey(event);

    return {
      ...acc,
      [key]: [...(acc[key] || []), event],
    };
  }, {});

const sortByStart = (events = []) =>
  [...events].sort((a, b) => {
    const startDiff = getEventTime(a, "start") - getEventTime(b, "start");

    if (startDiff) {
      return startDiff;
    }

    const endDiff = getEventTime(a, "end") - getEventTime(b, "end");

    if (endDiff) {
      return endDiff;
    }

    return a[originalIndexKey] - b[originalIndexKey];
  });

const setOverlapGroupZIndexes = (events = [], zIndexes) => {
  const foregroundEvents = events.filter(isForegroundEvent);

  events.forEach((event) => {
    if (isAvailabilityTime(event)) {
      zIndexes.set(event[originalIndexKey], BACKGROUND_Z_INDEX);
    }
  });

  foregroundEvents
    .sort((a, b) => {
      const startDiff = getEventTime(a, "start") - getEventTime(b, "start");

      if (startDiff) {
        return startDiff;
      }

      const durationDiff = getEventDuration(b) - getEventDuration(a);

      if (durationDiff) {
        return durationDiff;
      }

      return a[originalIndexKey] - b[originalIndexKey];
    })
    .forEach((event, index) => {
      zIndexes.set(event[originalIndexKey], FOREGROUND_Z_INDEX + index);
    });
};

const setScopedZIndexes = (events = []) => {
  const zIndexes = new Map();

  Object.values(groupByResourceAndDay(events)).forEach((groupedEvents) => {
    const sortedEvents = sortByStart(groupedEvents);
    let overlapGroup = [];
    let overlapGroupEnd = 0;

    const flushOverlapGroup = () => {
      if (overlapGroup.length) {
        setOverlapGroupZIndexes(overlapGroup, zIndexes);
      }
    };

    sortedEvents.forEach((event) => {
      const eventStart = getEventTime(event, "start");
      const eventEnd = getEventTime(event, "end");

      if (!overlapGroup.length || eventStart < overlapGroupEnd) {
        overlapGroup.push(event);
        overlapGroupEnd = Math.max(overlapGroupEnd, eventEnd);
      } else {
        flushOverlapGroup();
        overlapGroup = [event];
        overlapGroupEnd = eventEnd;
      }
    });

    flushOverlapGroup();
  });

  return events.map((event) => {
    const zIndex = zIndexes.get(event[originalIndexKey]);

    return {
      ...event,
      zIndex: zIndex === undefined ? FOREGROUND_Z_INDEX : zIndex,
    };
  });
};

export function prepareEvents(events = []) {
  const filterInvalidBreakTimes = (events = []) =>
    events.filter(
      (e) =>
        !(e.appointmentType === "break_time" && moment(e.start).isSame(e.end)),
    );

  return setScopedZIndexes(
    filterInvalidBreakTimes(events).map((event, originalIndex) => ({
      ...event,
      [originalIndexKey]: originalIndex,
    })),
  ).map(({ [originalIndexKey]: _originalIndex, ...event }) => event);
}

export const isAdditionalProviderAppointment = (details = {}) => {
  return details.appointment_type === "virtual" && !details.is_main_provider;
};

export const apiDateFormat = (date) => {
  return moment(date).format("YYYY-MM-DD");
};

export const showSelectedWeek = (date = moment()) => {
  let showSelectedLabel;
  let startOfWeek = moment(date).startOf("week");
  let ednOfWeek = moment(date).endOf("week");
  if (startOfWeek.format("YYYY") !== ednOfWeek.format("YYYY")) {
    showSelectedLabel =
      startOfWeek.format("MMM Do, YY - ") + ednOfWeek.format("MMM Do, YY");
  } else if (startOfWeek.format("MM") === ednOfWeek.format("MM")) {
    showSelectedLabel =
      startOfWeek.format("MMM ") +
      startOfWeek.format("Do - ") +
      ednOfWeek.format("Do, YYYY");
  } else {
    showSelectedLabel =
      startOfWeek.format("MMM Do - ") + ednOfWeek.format("MMM Do, YYYY");
  }
  return showSelectedLabel;
};

export const makeTitle = (obj) => {
  let titleArr = [],
    title = "";
  if (obj.appointment_services) {
    obj.appointment_services.map((objInner) => {
      if (objInner.service) {
        titleArr.push(objInner.service.name);
      }
    });
  }
  if (titleArr.length) {
    title = titleArr.join(", ");
  }
  return title;
};

export const startEndDateOfWeek = (date, format = "MM-DD-YYYY") => {
  return {
    start: moment(date, format).startOf("week"),
    end: moment(date, format).endOf("week"),
  };
};

export const viewDateFormat = (date) => {
  return moment(date).format("MMMM DD, YYYY");
};
